import styled from 'styled-components'
import { StyledExpandArrow } from '../../atoms/ExpandArrow/ExpandArrow.styles'

export const FilterSectionContainer = styled.div<{ $collapsed: boolean }>`
  margin-bottom: 16px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.default};

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.border.default};
  }
`

export const FilterSectionHeader = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px ${({ theme }) => theme.spacing.large};
  min-width: 0;
  cursor: pointer;
  touch-action: pan-y;
  user-select: none;
  transition: background ${({ theme }) => theme.transitions.default};

  ${({ $collapsed, theme }) =>
    $collapsed &&
    `
    &:hover {
      background: ${theme.colors.background.secondary};
    }
  `}
`

export const FilterSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

export const FilterSectionLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// Wraps the reorder grip and the section icon so the grip can cover the
// icon on large screens without shifting the title.
export const IconAnchor = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing.small};
`

export const SectionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) and (hover: hover) and (pointer: fine) {
    ${FilterSectionHeader}:hover:not([data-grip-dismissed]) & {
      opacity: 0;
    }
  }
`

// `touch-action: pan-y` — a finger can start a reorder from anywhere on
// the row. The header keeps the same rule so a quick swipe still scrolls.
// SortableFilterSections gates drag activation behind a 250ms long-press
// via TouchSensor, so a quick swipe scrolls while a deliberate hold-then-move
// picks up the section. The previous `touch-action: none` on the grip
// blocked browser scroll and made scrolling the filter list on iPhone /
// touchscreen laptops reorder the sections instead (Aldo 2026-05).
//
// Phones and tablets always show the grip beside the icon. On large
// screens with a mouse it leaves the flow and only appears over the
// section icon while the row is hovered or the grip has keyboard focus.
// Pointer release drops that focus and restores the icon until the
// pointer leaves the row (`data-grip-dismissed`).
export const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  color: ${({ theme }) => theme.colors.text.tertiary};
  padding: ${({ theme }) => theme.spacing.xsmall};
  margin-right: ${({ theme }) => theme.spacing.xsmall};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  touch-action: pan-y;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:active {
    cursor: grabbing;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) and (hover: hover) and (pointer: fine) {
    position: absolute;
    inset: 0;
    margin: 0;
    opacity: 0;
    pointer-events: none;
    background: transparent;
    z-index: 1;
    transition: opacity ${({ theme }) => theme.transitions.fast};

    ${FilterSectionHeader}:hover:not([data-grip-dismissed]) &,
    &:focus-visible {
      opacity: 1;
      pointer-events: auto;
      color: ${({ theme }) => theme.colors.text.secondary};
    }

    &:focus-visible + ${SectionIcon} {
      opacity: 0;
    }

    &:hover,
    &:focus-visible {
      background: transparent;
    }
  }
`

export const FilterSectionMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing.medium};
  margin-left: ${({ theme }) => theme.spacing.small};

  ${StyledExpandArrow} {
    color: ${({ theme }) => theme.colors.text.tertiary};
    transition: none;
  }
`

export const FilterSectionCount = styled.span<{ $active: boolean }>`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.tertiary)};
`

export const FilterSectionContent = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
  padding: ${({ theme }) => theme.spacing.large};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
  position: relative;
  overflow: visible;
  animation: ${({ $collapsed }) => ($collapsed ? 'none' : 'slideDown 0.2s ease-out')};

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`
